import { ref, nextTick, computed, watch } from "vue";
import { defineStore } from "pinia";
import { useConsoleLog as log } from "@/composables/console.js";
import { useAppStore } from "@/stores/app";
import { useNotificationsStore } from "@/stores/notifications";
import { useI18n } from "vue-i18n";
import { onBus } from "@/composables/eventBus";
import Comm from "@/composables/comm.js";
import Misc from "@/composables/misc.js";
import router from "@/router";

export const useUserStore = defineStore("user", () => {
  const lsPrefix = localStorage.getItem("cachePrefix") || "";
  const token = ref("");
  const idUser = ref("");
  const email = ref("");
  const idTenant = ref("");
  const tenant = ref(null);
  const usage = ref(null);
  const app = useAppStore();
  const i18n = useI18n();
  const translationLanguages = ref([]);
  const transcriptionLanguages = ref([]);
  const initCompleted = ref(false);
  let plan = null;
  let planPromise = null;

  const bc = new BroadcastChannel("C21MEDIACOPILOT");
  bc.onmessage = e => {
    const data = JSON.parse(e.data);

    // Other tab has requested the user token, emit it if available
    if (data.e == "EMIT_TOKEN" && token.value) {
      bc.postMessage(JSON.stringify({ e: "TOKEN", token: token.value }));
      log("Another tab requested token, sending it");
    }

    // Other tab has sent a token, use it if needed
    if (data.e == "TOKEN" && !token.value) {
      log("Another tab sent token, using it");
      saveToken(data.token);
      updateData();
    }
  };

  onBus("compositionSelected", data => {
    const tmp = {
      id: data.value.id,
      name: data.value.name
    };
    localStorage.setItem(
      lsPrefix + `composerLastOpenProject-${idTenant.value}`,
      JSON.stringify(tmp)
    );
  });

  function lastCompo() {
    const data = localStorage.getItem(
      lsPrefix + `composerLastOpenProject-${idTenant.value}`
    );
    if (!data) return null;
    return JSON.parse(data);
  }

  function clear() {
    token.value = "";

    idUser.value = "";
    email.value = "";
    idTenant.value = "";
    tenant.value = null;

    sessionStorage.removeItem("token");
  }

  function saveToken(userToken) {
    log("Saving token");
    token.value = userToken;

    sessionStorage.setItem("token", userToken);
  }

  function checkUsage() {
    if (usage.value.monthly_usage_seconds > usage.value.monthly_limit_seconds) {
      const notifications = useNotificationsStore();
      const id = `usageLimit-${new Date().getMonth() + 1}-${new Date().getFullYear()}`;

      notifications.add({
        id,
        date: new Date(),
        status: "unread",
        title: i18n.t("usage.limitBannerTitle"),
        message: i18n.t("usage.limitBannerHint", {
          current: Misc.roundDecimals(
            usage.value.monthly_usage_seconds / 3600,
            1
          ),
          limit: Misc.roundDecimals(usage.value.monthly_limit_seconds / 3600, 1)
        })
      });
    }
  }

  /**
   * Returns the current user plan.
   * @returns {Object} The current user plan.
   */
  async function currentPlan() {
    if (plan) return plan;

    // Reuse the in-flight request so concurrent callers (e.g. a route guard and
    // updateData on a page refresh) don't trigger duplicate plan fetches.
    if (planPromise) return planPromise;

    planPromise = (async () => {
      // On a fresh page load (e.g. a refresh on a plan-protected route) the user
      // data may not be loaded yet, so tenant.value is still null. Wait until
      // the tenant info is available before trying to fetch the plan.
      if (!tenant.value) {
        await new Promise(resolve => {
          const stop = watch(tenant, value => {
            if (value) {
              stop();
              resolve();
            }
          });
        });
      }

      const res = await Comm.get({ path: `/plans/${tenant.value.plan_id}` });
      plan = res.data;

      // Safeguard: if the API doesn't define certain feature flags, default them
      // to true so the corresponding features remain available.
      const defaultTrueFields = [
        "content_indexing",
        "metadata_generation",
        "video_clips",
        "video_compositions",
        "interactive_agent",
        "turbo_mode",
        "scene_face_analysis",
        "sound_detection"
      ];
      defaultTrueFields.forEach(field => {
        if (plan[field] === undefined || plan[field] === null)
          plan[field] = true;
      });

      return plan;
    })();

    try {
      return await planPromise;
    } finally {
      // Clear the in-flight reference; a successful call keeps `plan` cached, a
      // failed one allows a later retry.
      planPromise = null;
    }
  }

  /**
   * Checks if dubbing is enabled for the current user.
   * @returns {Boolean} True if dubbing is enabled, false otherwise.
   */
  async function dubbingEnabled() {
    const plan = await currentPlan();
    return plan.voice_over_languages_limit > 0;
  }

  /**
   * If the user wanted to access a protected page before logging in, this
   * function returns the path to that page. Otherwise it returns null.
   */
  function pathAfterLogin() {
    const path = localStorage.getItem("redirectAfterLogin");
    localStorage.removeItem("redirectAfterLogin");
    return path;
  }

  /**
   * Updates the user data.
   * @param {Object} params
   * @param {Function} params.cb Callback function to be called when the data is updated.
   * @param {String} params.thenGoTo Path to navigate to after the data is updated.
   */
  async function updateData({ cb = null, thenGoTo = null } = {}) {
    // Download user info from API.
    const res = await Comm.get({ path: "/user_info" });

    idUser.value = res.data.user.id;
    email.value = res.data.user.email;
    idTenant.value = res.data.tenant.id;
    tenant.value = res.data.tenant;
    usage.value = res.data.usage;

    // Plan data now includes which features are available for user so we need
    // to retrieve it now
    await currentPlan();

    // Set the LS prefix that will be used for all caches.
    const lsPrefix = idTenant.value.split("-").pop();
    localStorage.setItem("cachePrefix", lsPrefix + "-");

    // Check usage
    checkUsage();

    // Call callback if provided
    if (cb) cb();

    // Check if a redirection after login is needed
    let path = pathAfterLogin() || thenGoTo;
    if (path) {
      log("Redirecting to", path);
      await router.push(path);
    }
    initCompleted.value = true;
  }

  // When running embedded the token management is done by the app store.
  if (app.mode == "standalone") {
    if (sessionStorage.getItem("token")) {
      if (!token.value) {
        log("Token found in session storage");
        saveToken(sessionStorage.getItem("token"));
        nextTick(updateData);
      }
    } else initCompleted.value = true;
  }

  /**
   * Returns the list of available event presets for the current user/tenant.
   * In non-production environments, additional presets can be added via localStorage.
   */
  const availableEventPresets = computed(() => {
    const presets = [
      "cestapunta",
      "traineras",
      "f1",
      "futbol",
      "generic",
      "interview",
      "motogp",
      "tv"
    ];

    if (app.env != "pro" && app.env != "uat") {
      const lsData = localStorage.getItem("eventPresets");
      if (lsData) {
        try {
          const parsed = JSON.parse(lsData);
          if (Array.isArray(parsed)) {
            presets.push(...parsed);
          }
        } catch {
          // Ignore and use defaults
        }
      }
      return presets;
    }

    switch (idTenant.value) {
      // Tenant demo PRO
      case "01997f63-773f-7075-acde-dd4e4c0b217f":
      // Tenant Desarrollo C21 PRO
      // falls through
      case "01997f63-773f-7379-8b8f-e7f18413ea08":
      // Tenan demo UAT
      // falls through
      case "01992e26-6259-7f00-af70-b9dd7ff19035":
        return presets;

      // Tenant issue CIR-3307
      case "01992e26-6259-7c28-9ecb-c8a91fba9846":
        return ["tv"];
      // Dorna
      case "0199a039-7840-7185-a1ec-e23222546e6c":
        return ["motogp", "interview"];
      // EITB
      case "01997f63-773f-7ca4-ba0b-6ddfd4f82ac0":
        return ["cestapunta", "traineras", "interview"];
      // Eurovisión PRO
      case "019eab63-4782-7f64-8c3e-6d8f66b1145c":
        return ["generic", "futbol"];
      // 3Cat PRO
      case "019f7f4e-7771-76c8-bf0d-fe181702ef70":
        return ["futbol", "interview"];
    }
    return [];
  });

  return {
    token,
    email,
    idTenant,
    idUser,
    tenant,
    usage,
    saveToken,
    clear,
    updateData,
    translationLanguages,
    transcriptionLanguages,
    pathAfterLogin,
    initCompleted,
    lastCompo,
    currentPlan,
    dubbingEnabled,
    availableEventPresets
  };
});
